import React, { useState, useEffect, useRef } from "react";
import { Button, Checkbox, Col, Form, Input, Row, message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Blog } from "../../../../services/blog/blog";
import FileUpload, { FileUploadRef } from "../../../../components/file-input/FileUpload";
import axiosInstance from "../../../../api/axiosInstance";

const { TextArea } = Input;

const BlogDetailManagement = () => {
  const { blogID } = useParams();
  const [loading, setLoading] = useState(false);
  const [formBlog] = Form.useForm();
  const fileUploadRef = useRef<FileUploadRef>(null);
  const [imagePath, setImagePath] = useState<string>();
  const [hasNewImage, setHasNewImage] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (blogID) {
      setIsEditMode(true);
      axiosInstance.get(`/blogs/${blogID}`).then((response) => {
        const blog = response.data;
        formBlog.setFieldsValue(blog);
        setImagePath(blog.attachmentFile);
      });
    }
  }, [blogID, formBlog]);

  const handleUpload = async (): Promise<string | null> => {
    if (hasNewImage) {
      return await fileUploadRef.current?.uploadFile() || null;
    }
    return imagePath?.toString() ?? null;
  };

  const handleSaveBlog = async () => {
    try {
      const newImagePath = await handleUpload();
      const values = await formBlog.validateFields();

      setLoading(true);

      const payload = {
        blogName: values.blogName,
        blogCode: values.blogCode,
        content: values.content,
        description: values.description,
        isActive: values?.isActive,
        attachmentFile: newImagePath,
      };

      if (isEditMode && blogID) {
        // Cập nhật blog
        await axiosInstance.put(`/blogs/update-blog?blogID=${blogID}`, payload);
        message.success("Blog đã được cập nhật thành công!");
      } else {
        // Tạo blog mới
        await axiosInstance.post(`/blogs/create-blog`, payload);
        message.success("Blog đã được tạo thành công!");
      }

      navigate("/blog-management"); // Quay lại trang danh sách blog sau khi lưu
    } catch (error) {
      message.error("Đã xảy ra lỗi khi lưu blog.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/blog-management"); // Quay lại trang danh sách blog
  };

  return (
    <div style={{ padding: "20px" }}>
      <Spin spinning={loading}>
        <Form
          form={formBlog}
          layout="vertical"
          initialValues={{
            status: "draft",
            isPublished: false,
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Row>
                <Col span={24} className="flex justify-center items-center">
                  <div style={{ marginBottom: 20, height: 160, width: 260 }}>
                    <FileUpload
                      width={260}
                      height={160}
                      ref={fileUploadRef}
                      unit="blog"
                      type="blog"
                      initialFilePath={imagePath}
                      onFileSelected={() => setHasNewImage(true)}
                    />
                  </div>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Mã"
                    name="blogCode"
                    rules={[{ required: true, message: "Vui lòng nhập mã blog" }]}
                  >
                    <Input placeholder="Nhập mã blog" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    label="Tên blog"
                    name="blogName"
                    rules={[{ required: true, message: "Vui lòng nhập tên blog" }]}
                  >
                    <Input placeholder="Nhập tên blog" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Nội dung" name="content">
                    <CKEditor
                      editor={ClassicEditor}
                      data={formBlog.getFieldValue("content")}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        formBlog.setFieldsValue({ content: data });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Mô tả" name="description">
                    <TextArea rows={4} placeholder="Vui lòng nhập mô tả..." />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="isActive" valuePropName="checked">
                    <Checkbox>Được sử dụng</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: "right" }}>
            <Button
              onClick={handleCancel}
              style={{ marginRight: 10, backgroundColor: "#fff", borderColor: "#F48100", color: "#F48100" }}
            >
              Hủy
            </Button>
            <Button type="primary" style={{ marginBottom: 10, backgroundColor: "#F48100", borderColor: "#F48100" }} onClick={handleSaveBlog}>
              Lưu
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );
};

export default BlogDetailManagement;
